import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';

/**
 * Hook to fetch club details and verify user has management permissions
 * @param {string} clubId - Club ID from URL params
 * @param {object} currentUser - Current user object
 * @returns {object} { club, role, loading, hasPermission, error }
 */
export const useClubPermissions = (clubId, currentUser) => {
  const [club, setClub] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clubId || !currentUser?.id) {
      setLoading(false);
      setHasPermission(false);
      return;
    }

    fetchClubAndVerifyPermissions();
  }, [clubId, currentUser?.id]);

  const fetchClubAndVerifyPermissions = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch club details
      const { data: clubData, error: clubError } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', clubId)
        .single();

      if (clubError) {
        if (clubError.code === 'PGRST116') {
          throw new Error('Club not found');
        }
        throw clubError;
      }

      // Fetch user's role in this club
      const { data: roleData, error: roleError } = await supabase
        .from('players_club')
        .select('club_role')
        .eq('player_id', currentUser.id)
        .eq('club_id', clubId)
        .eq('is_active', true)
        .single();

      if (roleError) {
        if (roleError.code === 'PGRST116') {
          throw new Error('You are not a member of this club');
        }
        throw roleError;
      }

      // Check if user has management permissions
      const managementRoles = ['host', 'admin', 'owner'];
      const userHasPermission = managementRoles.includes(roleData.club_role);

      if (!userHasPermission) {
        throw new Error('You do not have permission to manage this club');
      }

      setClub(clubData);
      setRole(roleData.club_role);
      setHasPermission(true);
    } catch (err) {
      console.error('Error fetching club permissions:', err);
      setError(err.message);
      setHasPermission(false);
    } finally {
      setLoading(false);
    }
  };

  return { club, role, loading, hasPermission, error };
};

